import { getBrowserFamily, getViewportClass } from "./analyticsContext";
import { trackAnalyticsEvent } from "./analyticsClient";
import { bucketCharacterCount, mimeCategory, sanitizeErrorForAnalytics } from "./analyticsRedaction";
import type { AnalyticsScreen } from "./analyticsTypes";
import type { AnalyticsPayload } from "./analyticsTypes";
import { getSafeErrorCode } from "../app/safeError";

export interface ReportCounts {
  roomCount?: number;
  conditionCount?: number;
  readyConditionCount?: number;
  incompleteConditionCount?: number;
  mediaItemCount?: number;
  mediaBytesTotal?: number;
  totalCompressedMediaBytes?: number;
  thumbnailBytes?: number;
}

export function trackScreenView(input: {
  screen: AnalyticsScreen;
  previousScreen?: AnalyticsScreen;
  routePattern: string;
}): void {
  trackAnalyticsEvent(
    "screen_view",
    {
      screen: input.screen,
      previousScreen: input.previousScreen,
      routePattern: input.routePattern
    },
    { screen: input.screen, routePattern: input.routePattern }
  );
}

export function trackDraftCreated(): void {
  trackAnalyticsEvent("draft_created");
}

export function trackRoomCreated(counts: ReportCounts = {}): void {
  trackAnalyticsEvent("room_created", { ...counts });
}

export function trackRoomDeleted(counts: ReportCounts = {}): void {
  trackAnalyticsEvent("room_deleted", { ...counts });
}

export function trackConditionOpened(): void {
  trackAnalyticsEvent("condition_opened");
}

export function trackConditionDeleted(counts: ReportCounts = {}): void {
  trackAnalyticsEvent("condition_deleted", { ...counts });
}

export function trackPhotoPickerOpened(): void {
  trackAnalyticsEvent("photo_picker_opened");
}

export function trackPhotoSelected(file: File): void {
  trackAnalyticsEvent("photo_selected", {
    originalBytes: file.size,
    mimeCategory: mimeCategory(file.type)
  });
}

export function trackPhotoCompressStarted(file: File): void {
  trackAnalyticsEvent("photo_compress_started", {
    originalBytes: file.size,
    mimeCategory: mimeCategory(file.type)
  });
}

export function trackPhotoCompressSuccess(input: {
  originalBytes: number;
  compressedBytes: number;
  thumbBytes: number;
  width: number;
  height: number;
  mimeType?: string;
  elapsedMs: number;
}): void {
  trackAnalyticsEvent("photo_compress_success", {
    originalBytes: input.originalBytes,
    compressedBytes: input.compressedBytes,
    thumbBytes: input.thumbBytes,
    width: input.width,
    height: input.height,
    mimeCategory: mimeCategory(input.mimeType),
    elapsedMs: input.elapsedMs
  });
}

export function trackPhotoCompressFailed(error: unknown, originalBytes?: number, mimeType?: string): void {
  trackAnalyticsEvent(
    "photo_compress_failed",
    {
      ...sanitizeErrorForAnalytics(error, "PHOTO_COMPRESS_FAILED"),
      originalBytes,
      mimeCategory: mimeCategory(mimeType)
    },
    { priority: "critical", flush: true }
  );
}

export function trackPhotoSaveSuccess(input: {
  compressedBytes: number;
  thumbBytes: number;
  width: number;
  height: number;
}): void {
  trackAnalyticsEvent("photo_save_success", input);
  trackAnalyticsEvent("condition_created", {
    compressedBytes: input.compressedBytes,
    thumbBytes: input.thumbBytes,
    width: input.width,
    height: input.height
  });
}

export function trackPhotoSaveFailed(error: unknown, diagnostics: AnalyticsPayload = {}): void {
  trackAnalyticsEvent(
    "photo_save_failed",
    {
      ...diagnostics,
      ...sanitizeErrorForAnalytics(error, getSafeErrorCode(error, "PHOTO_SAVE_FAILED"))
    },
    {
      priority: "critical",
      flush: true
    }
  );
}

export function trackAudioRecordStarted(): void {
  trackAnalyticsEvent("audio_record_started");
}

export function trackAudioRecordStopped(durationMs: number): void {
  trackAnalyticsEvent("audio_record_stopped", { durationMs });
}

export function trackMicPermissionDenied(error?: unknown): void {
  trackAnalyticsEvent("mic_permission_denied", sanitizeErrorForAnalytics(error, "MIC_PERMISSION_DENIED"), {
    priority: "critical",
    flush: true
  });
}

export function trackAudioSaveSuccess(input: { durationMs: number; recordingBytes: number; mimeType: string }): void {
  trackAnalyticsEvent("audio_save_success", {
    durationMs: input.durationMs,
    recordingBytes: input.recordingBytes,
    mimeCategory: mimeCategory(input.mimeType)
  });
  trackAnalyticsEvent("transcription_queued", {
    durationMs: input.durationMs,
    recordingBytes: input.recordingBytes,
    mimeCategory: mimeCategory(input.mimeType)
  });
}

export function trackAudioSaveFailed(error: unknown): void {
  trackAnalyticsEvent("audio_save_failed", sanitizeErrorForAnalytics(error, "AUDIO_SAVE_FAILED"), {
    priority: "critical",
    flush: true
  });
}

export function trackTranscriptionRetryScheduled(input: { queueAgeMs?: number; safeErrorCode?: string }): void {
  trackAnalyticsEvent("transcription_retry_scheduled", input);
}

export function trackTranscriptionSuccess(input: {
  durationMs?: number;
  recordingBytes?: number;
  queueAgeMs?: number;
  elapsedMs?: number;
  modelUsed?: string;
}): void {
  trackAnalyticsEvent("transcription_success", input);
}

export function trackTranscriptionFailed(error: unknown, input: { queueAgeMs?: number; elapsedMs?: number } = {}): void {
  trackAnalyticsEvent(
    "transcription_failed",
    {
      ...sanitizeErrorForAnalytics(error, "TRANSCRIPTION_FAILED"),
      ...input
    },
    { priority: "critical", flush: true }
  );
}

export function trackNoteTyped(characterCount: number): void {
  trackAnalyticsEvent("note_typed", {
    characterCountBucket: bucketCharacterCount(characterCount),
    source: "typed"
  });
}

export function trackAiSuggestionAvailable(): void {
  trackAnalyticsEvent("ai_suggestion_available", { source: "audio_ai" });
}

export function trackAiSuggestionApplied(characterCount: number): void {
  trackAnalyticsEvent("ai_suggestion_applied", {
    characterCountBucket: bucketCharacterCount(characterCount),
    source: "audio_ai"
  });
}

export function trackNoteApproved(characterCount: number, source: "typed" | "audio_ai" | "edited_ai" = "typed"): void {
  const payload = {
    characterCountBucket: bucketCharacterCount(characterCount),
    source
  };
  trackAnalyticsEvent("note_approved", payload);
  trackAnalyticsEvent("note_marked_ready", payload);
}

export function trackExportScreenOpened(counts: ReportCounts): void {
  trackAnalyticsEvent("export_screen_opened", { ...counts }, { screen: "export", routePattern: "/export" });
}

export function trackPdfGenerateStarted(counts: ReportCounts): void {
  trackAnalyticsEvent("pdf_generate_started", withExportContext({ ...counts }));
}

export function trackPdfGenerateSuccess(
  counts: ReportCounts,
  input: { exportBytes: number; generationMs: number; pageCount?: number }
): void {
  trackAnalyticsEvent(
    "pdf_generate_success",
    withExportContext({
      ...counts,
      exportBytes: input.exportBytes,
      generationMs: input.generationMs,
      pageCount: input.pageCount
    }),
    { priority: "critical", flush: true }
  );
}

export function trackPdfGenerateFailed(error: unknown, counts: ReportCounts, generationMs?: number): void {
  trackAnalyticsEvent(
    "pdf_generate_failed",
    withExportContext({
      ...counts,
      ...sanitizeErrorForAnalytics(error, "PDF_GENERATE_FAILED"),
      generationMs
    }),
    { priority: "critical", flush: true }
  );
}

export function trackPdfDownloadClicked(counts: ReportCounts, exportBytes: number): void {
  trackAnalyticsEvent(
    "pdf_download_clicked",
    withExportContext({ ...counts, exportBytes }),
    { priority: "critical", flush: true }
  );
}

export function trackServiceWorkerError(error: unknown): void {
  trackAnalyticsEvent("service_worker_error", sanitizeErrorForAnalytics(error, "SERVICE_WORKER_ERROR"), {
    priority: "critical",
    flush: true
  });
}

function withExportContext(payload: Record<string, unknown>): Record<string, string | number | boolean | null | undefined> {
  return {
    ...payload,
    browserFamily: getBrowserFamily(),
    viewportClass: getViewportClass()
  } as Record<string, string | number | boolean | null | undefined>;
}
